import React, { useState, useEffect, useCallback } from 'react';
import { assetStatesApi } from '../../api';
import { AssetState, CreateAssetStateData, UpdateAssetStateData } from '../../types';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Card } from '../../components/common/Card';
import { Icons } from '../../components/common/Icons';
import { usePermissions } from '../../hooks/usePermissions';
import './AssetCategories.css';

export function AssetStates() {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('asset_states.manage');
  
  const [states, setStates] = useState<AssetState[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingState, setEditingState] = useState<AssetState | null>(null);
  const [formData, setFormData] = useState<CreateAssetStateData>({
    name: '',
    color: '#6b7280',
    description: '',
    is_default: false,
  });

  const fetchStates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await assetStatesApi.getStates({ include_inactive: showInactive });
      setStates(res.states);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load states');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    fetchStates();
  }, [fetchStates]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (editingState) {
        await assetStatesApi.updateState(editingState.id, formData as UpdateAssetStateData);
      } else {
        await assetStatesApi.createState(formData);
      }
      await fetchStates();
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save state');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this state?')) return;
    try {
      setLoading(true);
      await assetStatesApi.deleteState(id);
      await fetchStates();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete state');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (state: AssetState) => {
    setEditingState(state);
    setFormData({
      name: state.name,
      color: state.color,
      description: state.description || '',
      is_default: state.is_default,
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingState(null);
    setFormData({ name: '', color: '#6b7280', description: '', is_default: false });
  };

  return (
    <div className="asset-categories-page">
      <div className="page-header">
        <div className="page-title">
          <h1>Asset States</h1>
          <p>Manage asset states (Available, Assigned, Broken, etc.)</p>
        </div>
        {canManage && (
          <Button onClick={() => setShowModal(true)}>
            <Icons.Plus /> Add State
          </Button>
        )}
      </div>

      {error && (
        <div className="error-banner">
          <Icons.AlertCircle />
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      <Card>
        <div className="toolbar">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show Inactive
          </label>
        </div>

        {loading ? (
          <div className="loading-state">Loading states...</div>
        ) : states.length === 0 ? (
          <div className="empty-state">
            <Icons.Tag />
            <h3>No states found</h3>
            <p>Add your first state to get started</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Color</th>
                  <th>Description</th>
                  <th>Default</th>
                  <th>Status</th>
                  {canManage && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {states.map(state => (
                  <tr key={state.id} className={!state.is_active ? 'inactive-row' : ''}>
                    <td>
                      <span className="state-badge" style={{ backgroundColor: state.color }}>
                        {state.name}
                      </span>
                    </td>
                    <td>
                      <div className="color-preview">
                        <span className="color-swatch" style={{ backgroundColor: state.color }}></span>
                        <code>{state.color}</code>
                      </div>
                    </td>
                    <td>{state.description || '-'}</td>
                    <td>{state.is_default ? '✓' : '-'}</td>
                    <td>
                      <span className={`status-badge ${state.is_active ? 'active' : 'inactive'}`}>
                        {state.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {canManage && (
                      <td>
                        <div className="action-buttons">
                          <button className="icon-button" onClick={() => openEditModal(state)} title="Edit">
                            <Icons.Edit />
                          </button>
                          <button className="icon-button delete-btn" onClick={() => handleDelete(state.id)} title="Delete">
                            <Icons.Trash />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingState ? 'Edit State' : 'Add State'}</h2>
              <button className="close-button" onClick={resetForm}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Color</label>
                  <div className="color-input">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    />
                    <Input
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      placeholder="#10b981"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.is_default}
                      onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                    />
                    Set as default state for new assets
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <Button type="button" variant="secondary" onClick={resetForm}>Cancel</Button>
                <Button type="submit" disabled={loading}>{editingState ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AssetStates;
